import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { validateEmail } from 'utils/formValidators';
import Styling from 'components/AuthStyling';
import { loginUser } from 'reducers/auth';
import { getError, removeError } from 'reducers/ui';
import styles from './index.module.scss';
import { Link } from 'react-router-dom';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import Input, { STYLE_TYPE } from 'components/Forms/Fields/TextInput';
import Button from 'components/Button';
import Image from 'components/Image';
import passwordShow from 'images/icon_visible.svg';
import passwordHide from 'images/icon_invisible.svg';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import { getBrowserLocales } from 'utils/methods/helpers';
import packageContents from '../../../package.json';

const useStyles = makeStyles(theme => ({
  root: {
    height: '20px !important',
    width: '20px !important',
    color: theme.palette.secondary.light,
  },
}));

const Login = ({ error = '', loginUser, removeError, loading }) => {
  const { t, i18n } = useTranslation();
  // language of login should default to English or the language of the machine if it is available
  useEffect(() => {
    const lang = getBrowserLocales({ languageCodeOnly: true });
    // the browser api will return all the supported langauge by the browser with the prefered one being the first
    if (Array.isArray(lang)) {
      const [preferedLang] = lang;
      // if the browser supports the language api then change it according to the language
      i18n.changeLanguage(preferedLang); // default Language is broswer language
    } else {
      i18n.changeLanguage('en'); // default Language is english
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const classes = useStyles();
  /* Local state */
  // should never be undefined as it can lead to uncontrolled input state
  const [user, setUser] = useState({
    email: '',
    password: '',
  });
  const [touched, setTouched] = useState({});
  const [formValid, setFormValid] = useState({});
  const [showPwd, setshowPwd] = useState(false);
  const [errorText, setErrorText] = useState({});

  // FIX-ME: Refactor the validation field part
  const validateField = (type, value) => {
    // three scenarios:
    if (type === 'email') {
      if (value) {
        // valid
        if (validateEmail(value)) {
          setFormValid({ ...formValid, email: true });
        } else {
          // email is invalid
          setFormValid({ ...formValid, email: false });
          setErrorText({ ...errorText, email: 'Email is invalid' });
        }
      } else {
        // email is empty
        setFormValid({ ...formValid, email: false });
        setErrorText({ ...errorText, email: 'Email is required' });
      }
    }

    if (type === 'password') {
      if (value.length <= 0) {
        //for log in field we only care that the value is entered.  The length/rules are for creation only
        setFormValid({ ...formValid, password: false });
        setErrorText({ ...errorText, password: 'Password is required' });
      } else {
        setFormValid({ ...formValid, password: true });
      }
    }
  };

  const updateUserLogin = e => {
    const fieldName = e.target.name;
    const fieldValue = e.target.value;

    setUser({ ...user, [fieldName]: fieldValue });
    setTouched({ ...touched, [fieldName]: true });

    validateField(fieldName, fieldValue);

    if (error) {
      removeError();
    }
  };

  /* Handler */

  const submit = e => {
    e.preventDefault();
    const token = window.sessionStorage.getItem('fcm_token');
    if (formValid.email && user.password) {
      loginUser({
        ...user,
        token,
        app: 'PORTAL',
        appVersion: packageContents?.version,
      });
    }
  };

  const showPassword = () => {
    setshowPwd(!showPwd);
  };

  const handleMouseDownPwd = event => {
    event.preventDefault();
  };
  return (
    <Styling
      message="Welcome back! Please log in to your account."
      dataTestid={'loginForm'}
      navigateToHome={false}
    >
      <form className={styles.formContent} noValidate onSubmit={submit}>
        <div className={styles.formArea}>
          <FormControl className={styles.formCntrl}>
            <Input
              id="login-email"
              className={styles.blackText}
              field="email"
              type="email"
              error={!formValid.email && touched.email && errorText.email}
              value={user.email}
              onChange={updateUserLogin}
              placeholder="Email"
              styleType={STYLE_TYPE.loginInput}
            />
          </FormControl>
          <FormControl className={styles.formCntrl}>
            <Input
              id="pwd"
              className={styles.blackText}
              field="password"
              value={user.password}
              type={showPwd ? 'text' : 'password'}
              onChange={updateUserLogin}
              placeholder="Password"
              error={
                !formValid.password && touched.password && errorText.password
              }
              styleType={STYLE_TYPE.loginInput}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={showPassword}
                    onMouseDown={handleMouseDownPwd}
                  >
                    <Image
                      className={styles.pwdIcon}
                      alt=""
                      src={showPwd ? passwordShow : passwordHide}
                    />
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
          <Link className={styles.pwdRecoverLink} to="/pwd-recover">
            {t('Forgot Password')}
          </Link>
        </div>
        {error && <div className={styles.textError}>{error}</div>}
        <div className={styles.btnContainer}>
          <Button
            type="submit"
            onClick={submit}
            text={'Log in'}
            {...(loading && {
              icon: (
                <CircularProgress
                  classes={{
                    root: classes.root,
                  }}
                />
              ),
            })}
          />
        </div>
      </form>
    </Styling>
  );
};

Login.propTypes = {
  error: PropTypes.string,
  history: PropTypes.object.isRequired,
  loginUser: PropTypes.func.isRequired,
  removeError: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

const mapStateToProps = state => ({
  error: getError(state),
  loading: state.ui.loading,
});

const mapDispatchToProps = dispatch => ({
  loginUser: user => dispatch(loginUser(user)),
  removeError: () => dispatch(removeError()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Login);
